import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useRef, forwardRef, useState, useCallback, useImperativeHandle, Children, cloneElement } from "react";
import onClickOutside from "react-onclickoutside";
import { FocusTrap } from "./FocusTrap.js";
import { useUniqueId } from "./hooks/useUniqueId.js";
import { useBoundingRects, computePopupPosition } from "./internal/popup-utils.js";
import { PopupPortal } from "./internal/PopupPortal.js";
import { useThemeFromContext } from "./theme.js";
const Popup = onClickOutside(function(props) {
  var _a;
  const { children, popupProps, side = "right", targetId } = props;
  const { colors } = useThemeFromContext();
  const elementRef = useRef(null);
  const [targetBounds, elementBounds, viewportBounds] = useBoundingRects([
    { id: targetId },
    { ref: elementRef },
    { viewport: true }
  ]);
  const { position } = computePopupPosition({
    elementSize: elementBounds,
    gap: 10,
    preferredSide: side,
    targetPosition: targetBounds,
    viewportSize: viewportBounds
  });
  return jsx(PopupPortal, { children: jsx("div", { ref: elementRef, ...popupProps, style: {
    border: `1px solid ${colors.dark(0.55)}`,
    borderRadius: 4,
    backgroundColor: "white",
    boxShadow: "0 3px 2px 0 rgba(0,0,0,0.12)",
    ...popupProps.style,
    position: "fixed",
    top: 0,
    left: 0,
    transform: `translate(${position.left}px, ${position.top}px)`,
    visibility: !viewportBounds.width ? "hidden" : (_a = popupProps.style) == null ? void 0 : _a.visibility
  }, children }) });
});
const PopupTrigger = forwardRef((props, ref) => {
  const { children, closeOnClick = false, content, popupProps: { role = "dialog", ...otherPopupProps } = {}, side = "top", onChange } = props;
  const [open, _setOpen] = useState(false);
  const setOpen = useCallback((open2) => {
    _setOpen(open2);
    onChange == null ? void 0 : onChange(open2);
  }, [onChange]);
  const close = useCallback(() => setOpen(false), [setOpen]);
  useImperativeHandle(ref, () => ({
    close: () => setOpen(false)
  }));
  const child = Children.only(children);
  const defaultChildId = useUniqueId();
  const childId = child.props.id || defaultChildId;
  const popupId = useUniqueId();
  const labelledby = child.props["aria-labelledby"] || childId;
  return jsxs(Fragment, { children: [cloneElement(child, {
    "aria-controls": open ? popupId : void 0,
    "aria-expanded": open,
    "aria-haspopup": role,
    "aria-owns": open ? popupId : void 0,
    // Add childId as a class to match Popup's outsideClickIgnoreClass.
    className: `${child.props.className || ""} ${childId}`,
    id: childId,
    onClick: (e) => {
      var _a, _b;
      (_b = (_a = child.props).onClick) == null ? void 0 : _b.call(_a, e);
      setOpen(!open);
    }
  }), open && jsx(Popup, { outsideClickIgnoreClass: childId, popupProps: {
    // aria-modal is only relevant for dialog popups.
    "aria-modal": role === "dialog" ? true : void 0,
    ...otherPopupProps,
    "aria-labelledby": labelledby,
    id: popupId,
    role,
    onClick: closeOnClick ? close : void 0
  }, side, targetId: childId, handleClickOutside: close, children: jsx(FocusTrap, { onEscape: close, children: content }) })] });
});
PopupTrigger.displayName = "PopupTrigger";
export {
  PopupTrigger
};
